import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ArrowRight, Filter, RefreshCw } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Progress } from '../components/ui/progress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  queued: { color: '#8A8A9A', dot: 'status-dot-queued', label: 'Queued' },
  processing: { color: '#FF6B4A', dot: 'status-dot-processing', label: 'Processing' },
  completed: { color: '#FF6B4A', dot: 'status-dot-active', label: 'Completed' },
  failed: { color: '#EF4444', dot: 'status-dot-error', label: 'Failed' },
};

const SERVICE_LABELS = {
  ai_image: 'AI Image',
  '3d_render': '3D Render',
  data_viz: 'Data Viz',
  video_render: 'Video',
};

export default function JobsList() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [refreshing, setRefreshing] = useState(false);

  const fetchJobs = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await axios.get(`${API}/jobs`, { params, withCredentials: true });
      setJobs(res.data);
    } catch (err) { /* polling errors are non-critical */ }
    finally { setLoading(false); setRefreshing(false); }
  }, [statusFilter]);

  useEffect(() => {
    fetchJobs();
    const interval = setInterval(fetchJobs, 5000);
    return () => clearInterval(interval);
  }, [fetchJobs]);

  const handleRefresh = () => { setRefreshing(true); fetchJobs(); };

  const formatDate = (iso) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-5 h-5 text-[#8A8A9A] animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="jobs-list-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#8A8A9A] mb-2">Queue</p>
          <h1 className="font-display text-3xl font-light tracking-tight text-[#1A1A2E]" data-testid="jobs-heading">
            {jobs.length} job{jobs.length !== 1 ? 's' : ''}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-32 bg-[#FFFFFF] border-gray-200 text-[#1A1A2E] rounded-md text-xs h-8" data-testid="status-filter">
              <Filter className="w-3 h-3 mr-1.5 text-[#8A8A9A]" />
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent className="bg-[#FFFFFF] border-gray-200 text-[#1A1A2E]">
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="queued">Queued</SelectItem>
              <SelectItem value="processing">Processing</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={handleRefresh} className="border-gray-200 text-[#8A8A9A] hover:text-[#1A1A2E] rounded-md h-8 w-8" data-testid="refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {jobs.length > 0 ? (
        <div className="rounded-lg overflow-hidden" style={{ background: '#FFFFFF', border: '1px solid rgba(255,255,255,0.04)' }}>
          <Table>
            <TableHeader>
              <TableRow className="border-gray-100 hover:bg-transparent">
                <TableHead className="text-[#8A8A9A] font-mono text-[10px] tracking-wider uppercase">Status</TableHead>
                <TableHead className="text-[#8A8A9A] font-mono text-[10px] tracking-wider uppercase">Job</TableHead>
                <TableHead className="text-[#8A8A9A] font-mono text-[10px] tracking-wider uppercase hidden sm:table-cell">Service</TableHead>
                <TableHead className="text-[#8A8A9A] font-mono text-[10px] tracking-wider uppercase hidden md:table-cell">Progress</TableHead>
                <TableHead className="text-[#8A8A9A] font-mono text-[10px] tracking-wider uppercase hidden lg:table-cell">Cost</TableHead>
                <TableHead className="text-[#8A8A9A] font-mono text-[10px] tracking-wider uppercase hidden lg:table-cell">Date</TableHead>
                <TableHead className="w-8"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.map((job) => {
                const sc = STATUS_CONFIG[job.status] || STATUS_CONFIG.queued;
                return (
                  <TableRow key={job.job_id} className="border-gray-100 hover:bg-gray-50" data-testid={`job-row-${job.job_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className={`status-dot ${sc.dot}`} />
                        <Badge className="text-[10px] rounded-md px-1.5 py-0.5 bg-transparent border hidden sm:inline-flex" style={{ color: sc.color, borderColor: `${sc.color}25` }}>
                          {sc.label}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell><span className="text-xs text-[#1A1A2E] truncate block max-w-[180px]">{job.title}</span></TableCell>
                    <TableCell className="hidden sm:table-cell"><span className="font-mono text-[10px] text-[#8A8A9A]">{SERVICE_LABELS[job.service_type]}</span></TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="flex items-center gap-2 w-28">
                        <Progress value={job.progress} className="h-1 bg-[#FFF0EA] [&>div]:bg-[#FF6B4A]" />
                        <span className="font-mono text-[10px] text-[#8A8A9A]">{job.progress}%</span>
                      </div>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell"><span className="font-mono text-[10px] text-[#FF6B4A]">{job.cost} cr</span></TableCell>
                    <TableCell className="hidden lg:table-cell"><span className="text-[10px] text-[#8A8A9A]">{formatDate(job.created_at)}</span></TableCell>
                    <TableCell>
                      <Link to={`/dashboard/jobs/${job.job_id}`}>
                        <ArrowRight className="w-3.5 h-3.5 text-[#8A8A9A] hover:text-[#1A1A2E] transition-colors" />
                      </Link>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="text-center py-20 rounded-lg" style={{ background: '#FFFFFF', border: '1px solid rgba(255,255,255,0.04)' }}>
          <p className="text-[#8A8A9A] text-sm mb-4">No jobs found.</p>
          <Link to="/dashboard/submit">
            <Button className="bg-[#FF6B4A] text-white font-medium rounded-md text-xs h-9 px-5" data-testid="empty-jobs-submit">Submit Job</Button>
          </Link>
        </div>
      )}
    </div>
  );
}
